/**
 * Safe wrappers around Web Storage. Storage can be missing or throw (private windows, blocked
 * site data, quota). Nothing authoritative is ever kept here (docs/02 section 3).
 */

type StorageKind = "session" | "local";

function area(kind: StorageKind): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

export function readJson<T>(kind: StorageKind, key: string): T | null {
  try {
    const raw = area(kind)?.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeJson(kind: StorageKind, key: string, value: unknown): void {
  try {
    area(kind)?.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / unavailable storage */
  }
}

export function remove(kind: StorageKind, key: string): void {
  try {
    area(kind)?.removeItem(key);
  } catch {
    /* ignore */
  }
}

export const STORAGE_KEYS = {
  reconnect: "townsy.reconnect.v1",
  signDedupe: "townsy.signdedupe.v1",
  panelDedupe: "townsy.paneldedupe.v1",
  quality: "townsy.quality.v1",
  dismissedNotices: "townsy.notices.v1",
  /** Avatar chosen at sign-up that could not be saved yet (no session); applied after sign-in. */
  pendingAppearance: "townsy.pendingAppearance.v1",
  /** A guest's muted people (actor ids) in this browser; accounts keep mutes on the server (ADR-0013). */
  mutedActors: "townsy.muted.v1",
} as const;
